import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import useScrollDirection from '../../util/useScrollDirection';

import { LinkedLogo } from '../../components';

import css from './TopbarSimplified.module.css';

const MAX_MOBILE_SCREEN_WIDTH = 1024;

/**
 * A component that renders a simplified top bar. This is used on situations,
 * where we don't want the user to be distacted by the multiple links in the Topbar component.
 * This is used in CheckoutPage, MakeOfferPage, and RequestQuotePage.
 *
 * Note: the real Topbar component can be found from src/containers/TopbarContainer/
 *
 * @component
 * @param {Object} props
 * @param {string} props.className - The class name for the topbar
 * @param {string} props.rootClassName - The root class name for the topbar
 * @param {string} props.mobileRootClassName - Overwrite mobile layout root classes
 * @param {string} props.mobileClassName - Add more style rules for mobile layout
 * @param {string} props.desktopClassName - Add more style rules for desktop layout
 * @param {('default' | 'heroOverlay')?} props.mobileVariant - Mobile topbar layout variant
 * @param {boolean} props.hideOnScroll - Hide topbar on scroll down, show on scroll up
 * @returns {JSX.Element}
 */
const TopbarSimplified = props => {
  const [mounted, setMounted] = useState(false);
  const [isMobileLayout, setIsMobileLayout] = useState(true);
  const intl = useIntl();
  const config = useConfiguration();

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    let mediaQueryList = null;
    let updateIsMobileLayout = null;

    if (mounted) {
      mediaQueryList = window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`);
      setIsMobileLayout(mediaQueryList.matches);

      updateIsMobileLayout = e => {
        setIsMobileLayout(e.matches);
      };
      mediaQueryList.addEventListener('change', updateIsMobileLayout);
    }

    return () => {
      if (mediaQueryList && updateIsMobileLayout) {
        mediaQueryList.removeEventListener('change', updateIsMobileLayout);
      }
    };
  }, [mounted]);

  const {
    className,
    rootClassName,
    mobileRootClassName,
    mobileClassName,
    desktopClassName,
    mobileVariant = 'default',
    // Hide on scroll down, reveal on scroll up. Enabled by default so the effect
    // is consistent across the whole site; pass hideOnScroll={false} to opt out.
    hideOnScroll = true,
  } = props;
  const linkToExternalSite = config?.topbar?.logoLink;
  const marketplaceName = config.marketplaceName;

  const isTopbarVisible = useScrollDirection(hideOnScroll);
  const rootRef = useRef(null);

  useLayoutEffect(() => {
    if (!hideOnScroll || !rootRef.current) {
      return undefined;
    }

    const root = rootRef.current;
    root.style.transition = 'transform 0.3s ease';
    root.classList.toggle(css.topbarRootHidden, !isTopbarVisible);

    return () => {
      root.classList.remove(css.topbarRootHidden);
      root.style.transition = '';
    };
  }, [hideOnScroll, isTopbarVisible]);

  const isHeroOverlayMobile = isMobileLayout && mobileVariant === 'heroOverlay';

  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={classes} ref={rootRef}>
      <nav
        className={classNames(mobileRootClassName || css.container, mobileClassName, {
          [css.containerHeroOverlay]: isHeroOverlayMobile,
        })}
      >
        <LinkedLogo
          id="logo-topbar-mobile"
          className={
            isHeroOverlayMobile ? css.mobileLogoLinkHeroOverlay : css.mobileLogoLink
          }
          logoImageClassName={isHeroOverlayMobile ? css.heroOverlayLogoImage : undefined}
          logoImageMobile={isHeroOverlayMobile ? '/static/img/logo.svg' : undefined}
          layout="mobile"
          alt={intl.formatMessage({ id: 'Topbar.logoIcon' })}
          linkToExternalSite={linkToExternalSite}
        />
      </nav>
      <div className={css.desktop}>
        <nav
          className={classNames(css.desktopNav, desktopClassName)}
          aria-label={intl.formatMessage({ id: 'TopbarSimplified.goToLandingPage' })}
        >
          <LinkedLogo
            id="logo-topbar-desktop"
            className={classNames(css.logoLink,css.centerLogo)}
            layout="desktop"
            alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
            linkToExternalSite={linkToExternalSite}
          />
        </nav>
      </div>
    </div>
  );
};

export default TopbarSimplified;
